import {
  ApartmentOutlined,
  BankOutlined,
  CrownOutlined,
  ShareAltOutlined,
  ShopOutlined,
  TeamOutlined,
  ToolOutlined,
} from '@ant-design/icons';
import _ from 'lodash';

import { ACTOR_KINDS, getDefaultRequiredStepsForActorKind } from './utils';

export const ACTOR_KIND_META = Object.freeze({
  [ACTOR_KINDS.DISTRIBUTOR]: { Icon: ApartmentOutlined },
  [ACTOR_KINDS.PARTNER]: { Icon: TeamOutlined },
  [ACTOR_KINDS.RESELLER]: { Icon: ShopOutlined },
  [ACTOR_KINDS.REFERRAL_AGENT]: { Icon: ShareAltOutlined },
  [ACTOR_KINDS.OPERATOR]: { Icon: ToolOutlined },
  [ACTOR_KINDS.CUSTOMER]: { Icon: BankOutlined },
  [ACTOR_KINDS.AUTOMY_INTERNAL]: { Icon: CrownOutlined },
});

const normalizeRequiredSteps = (requiredSteps, actorKind) => {
  if (Array.isArray(requiredSteps) && requiredSteps.length) {
    return requiredSteps.filter((step) => typeof step === 'string' && step.length);
  }
  return getDefaultRequiredStepsForActorKind(actorKind);
};

export const FALLBACK_REGISTRATION_OPTIONS = Object.values(ACTOR_KINDS).map((actorKind) => ({
  actorKind,
  title: null,
  description: null,
  visible: true,
  enabled: actorKind !== ACTOR_KINDS.AUTOMY_INTERNAL,
  disabledReason: null,
  requiredSteps: getDefaultRequiredStepsForActorKind(actorKind),
  requiresCommercialAgreement: false,
  requiresBillingConfiguration: false,
  requiresParentChannelActor: false,
}));

export const normalizeRegistrationOptions = (response) => {
  const rawOptions = response?.options ?? response?.items ?? response;
  if (!Array.isArray(rawOptions)) return FALLBACK_REGISTRATION_OPTIONS;

  return rawOptions
    .map((option) => {
      const actorKind = option?.actorKind ?? option?.value;
      if (!actorKind || !ACTOR_KIND_META[actorKind]) return null;

      return {
        actorKind,
        title: option?.title || null,
        description: option?.description || null,
        visible: option?.visible !== false,
        enabled: option?.enabled !== false,
        disabledReason: option?.disabledReason || null,
        requiredSteps: normalizeRequiredSteps(option?.requiredSteps, actorKind),
        requiresCommercialAgreement: option?.requiresCommercialAgreement === true,
        requiresBillingConfiguration: option?.requiresBillingConfiguration === true,
        requiresParentChannelActor: option?.requiresParentChannelActor === true,
      };
    })
    .filter(Boolean);
};

export const getRegistrationOption = (registrationOptions = [], actorKind) =>
  registrationOptions.find((option) => option.actorKind === actorKind) ?? null;

export const getActorKindMeta = (actorKind) => ACTOR_KIND_META[actorKind] || null;

export const getRegistrationOptionTitle = (registrationOptions = [], actorKind) => {
  const option = getRegistrationOption(registrationOptions, actorKind);
  return option?.title || null;
};

/** Backend title, then `channelActorKind.*` locale, then raw actorKind. */
export const resolveActorKindDisplayLabel = (
  formatMessage,
  actorKind,
  registrationOptions = [],
) => {
  const optionTitle = getRegistrationOptionTitle(registrationOptions, actorKind);
  if (optionTitle) return optionTitle;

  if (!actorKind) return null;
  const translationKey = `channelActorKind.${_.camelCase(actorKind)}`;
  const label = formatMessage({ id: translationKey });
  return label === translationKey ? actorKind : label;
};

export const getVisibleRegistrationOptions = (registrationOptions = []) =>
  registrationOptions.filter((option) => option.visible);

export const mergeRegistrationOptionMeta = (registrationOptions = []) =>
  getVisibleRegistrationOptions(registrationOptions).map((option) => ({
    ...option,
    ...getActorKindMeta(option.actorKind),
  }));
